import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { SubmitEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { Autocomplete, Divider, FormHelperText, FormLabel, IconButton, TextField } from '@mui/material';
import { GET_ITEMS_FOR_PO_QUERY } from '../../queries/ItemQueries';
import { useQuery } from '@apollo/client/react';
import { GET_VENDORS_QUERY } from '../../queries/VendorQueries';
import type { PurchaseOrder } from '../../models/PurchaseOrder';
import InventoryItemForm, { InventoryItemFormState } from '../inventoryItems/InventoryItemForm';
import { Add, Delete } from '@mui/icons-material';
import { red } from '../../theme/themePrimitives';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickerValue } from '@mui/x-date-pickers/internals';
import { InventoryItem } from '../../models/InventoryItem';
import { Vendor } from "../../models/Vendor";

export interface PurchaseOrderFormState {
  values: Partial<Omit<PurchaseOrder, 'id'>>;
  errors: Partial<Record<keyof PurchaseOrderFormState['values'], string>>;
}

export interface PurchaseOrderFormProps {
  formState: PurchaseOrderFormState;
  inventoryItemErrors?: Map<number, Partial<Record<keyof InventoryItemFormState['values'], string>>>;
  onFieldChange: (
    name: keyof PurchaseOrderFormState['values'],
    value: FormFieldValue,
  ) => void;
  onInventoryItemFieldChange: (items: InventoryItem[]) => void;
  onSubmit: (formValues: Partial<PurchaseOrderFormState['values']>, inventoryItems: InventoryItem[]) => Promise<void>;
  onReset?: (formValues: Partial<PurchaseOrderFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function PurchaseOrderForm(props: PurchaseOrderFormProps) {
  const {
    formState,
    inventoryItemErrors = new Map<number, Partial<Record<keyof InventoryItemFormState['values'], string>>>(),
    onFieldChange,
    onInventoryItemFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const { data: itemsData } = useQuery(
    GET_ITEMS_FOR_PO_QUERY);

  const { data: vendorsData, loading: vendorsLoading } = useQuery(
    GET_VENDORS_QUERY);

  const [poItems, setPOItems] = useState<InventoryItem[]>(formValues.inventoryItems || []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues, poItems);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit, poItems],
  );

  const handleDatePurchasedFieldChange = useCallback(
    (value: PickerValue) => {
      onFieldChange(
        'dateTimePurchased',
        value,
      );
    },
    [onFieldChange],
  );

  const handleInventoryItemFormFieldChange = useCallback(
    (updatedInventoryItem: Partial<InventoryItemFormState['values']>, index: number) => {
      const updatedItems = [...poItems];

      updatedItems[index] = { ...poItems[index], ...updatedInventoryItem };

      setPOItems(updatedItems);

      onInventoryItemFieldChange(updatedItems);
    },
    [onInventoryItemFieldChange, poItems],
  );

  const addNewItem = useCallback(() => {
    setPOItems(
      [...poItems, {
        id: 0,
        itemId: 0,
        isArchived: false,
        vendorId: 0,
        costPerUnit: 0,
        purchaseOrderId: 0,
        quantityOnHand: 0,
        quantityOrdered: 0,
        lotNumber: ''
      }]);
  }, [poItems]);

  const removeItem = useCallback((indexToRemove: number) => {
    const updatedIngredients = poItems.filter((_, index) => index !== indexToRemove);
    setPOItems(updatedIngredients);
  }, [poItems]);

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/inventory-items');
  }, [navigate, backButtonPath]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 9, sm: 3 }} sx={{ display: 'flex' }}>
            <Autocomplete
              getOptionLabel={(option) => option.name}
              options={vendorsData?.vendors || []}
              loading={vendorsLoading}
              onChange={(_, newValue: Vendor | null) => {
                onFieldChange(
                  'vendorId',
                  +(newValue?.id || 0)
                );
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Vendor"
                  error={!!formErrors.vendorId}
                  helperText={formErrors.vendorId ?? ' '}
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
              renderOption={(props, vendor) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                  >
                    {vendor.name}
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 9, sm: 3 }} sx={{ display: 'flex' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formValues.dateTimePurchased}
                onChange={handleDatePurchasedFieldChange}
                label="Date Purchased"
                name="dateTimePurchased"
                slotProps={{
                  textField: {
                    required: true,
                    error: !!formErrors.dateTimePurchased,
                    helperText: !!formErrors.dateTimePurchased || ' ',
                    InputLabelProps: {
                      shrink: true
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Grid container spacing={0} sx={{ mb: 2, width: '100%' }}>
          <Stack spacing={2}>
            <FormLabel error={!!formErrors.inventoryItems}>
              Items
            </FormLabel>
            <div>
              <Button
                type="button"
                color="secondary"
                startIcon={<Add />}
                variant="outlined"
                onClick={addNewItem}>
                Add Item
              </Button>
            </div>
            {!!formErrors.inventoryItems ?
              <FormHelperText error={!!formErrors.inventoryItems}>
                {formErrors.inventoryItems ?? ' '}
              </FormHelperText> : <></>
            }
          </Stack>
        </Grid>
        <Box sx={{ maxHeight: 400, overflowY: 'auto', paddingTop: 2, paddingLeft: 1, paddingRight: 1, width: '95%' }}>
          {poItems?.map((item: InventoryItem, index: number) => (
            <div key={index}>
              <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                <InventoryItemForm
                  items={itemsData?.items || []}
                  initialValue={item}
                  initialErrors={inventoryItemErrors.get(index)}
                  onFormValuesUpdate={(inventoryItemFormValues) => handleInventoryItemFormFieldChange(inventoryItemFormValues, index)}
                  showActionButtons={false} />
                <Box sx={{ width: '36px' }}>
                  <IconButton
                    size="small"
                    style={{ marginTop: '8px' }}
                    onClick={() => removeItem(index)}>
                    <Delete htmlColor={red[400]} />
                  </IconButton>
                </Box>
              </Stack>
              <Divider sx={{ marginBottom: (index === (poItems.length - 1)) ? 0 : 4 }} />
            </div>
          ))}
        </Box>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          {submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}
